import React, { useState, useEffect } from 'react';
import { getAdminTickets, getAdminTicketDetails, updateTicketStatus, adminReplyToTicket } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { showSuccessToast, showErrorToast } from '../../utils/toast';
import { formatDateTimeDDMMYYYY } from '../../utils/dateUtils';

const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => { loadTickets(); }, []);

  const loadTickets = async () => {
    try { const res = await getAdminTickets(); setTickets(res.data || []); } 
    catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const viewTicket = async (ticket) => {
    try {
      const res = await getAdminTicketDetails(ticket.ticketId);
      setSelected(res.data);
      setStatus(res.data.status);
    } catch (err) { showErrorToast('Failed to load ticket details'); }
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    try {
      await adminReplyToTicket(selected.ticketId, { message: reply });
      setReply('');
      viewTicket(selected);
      showSuccessToast('Reply sent');
    } catch (err) { showErrorToast('Failed'); }
  };

  const handleStatusUpdate = async () => {
    try {
      await updateTicketStatus(selected.ticketId, { status });
      showSuccessToast('Status updated');
      loadTickets();
    } catch (err) { showErrorToast('Failed'); }
  };

  const getStatusColor = (s) => {
    const colors = { OPEN: 'bg-red-100 text-red-800', IN_PROGRESS: 'bg-blue-100 text-blue-800', RESOLVED: 'bg-green-100 text-green-800', CLOSED: 'bg-gray-100 text-gray-800' };
    return colors[s] || 'bg-gray-100';
  };

  if (loading) return <div className="flex justify-center p-8">Loading...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Support Tickets</h1>
      {tickets.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-gray-500">No tickets</CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {tickets.map((t) => {
            const registeredBy = t.merchantName || t.merchant?.merchantName || t.createdByName || t.userName || (t.merchantId ? `Merchant ID: ${t.merchantId}` : 'Merchant');
            const createdDateStr = t.createdAt ? `${formatDateTimeDDMMYYYY(t.createdAt)} ${new Date(t.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}` : 'N/A';
            return (
              <Card key={t.id} className="cursor-pointer hover:bg-gray-50" onClick={() => viewTicket(t)}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900">{t.subject}</p>
                    <div className="flex flex-wrap items-center gap-x-3 text-xs text-gray-500 mt-1">
                      <span>ID: <strong className="text-gray-700">{t.ticketId}</strong></span>
                      <span>•</span>
                      <span>Registered By: <strong className="text-gray-700">{registeredBy}</strong></span>
                      <span>•</span>
                      <span>Created: <strong className="text-gray-700">{createdDateStr}</strong></span>
                    </div>
                  </div>
                  <Badge className={getStatusColor(t.status)}>{t.status}</Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{selected?.subject}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Ticket Info Card */}
            {selected && (
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <span className="text-gray-500 text-xs block">Ticket ID</span>
                  <strong className="text-gray-900">{selected.ticketId}</strong>
                </div>
                <div>
                  <span className="text-gray-500 text-xs block">Registered By</span>
                  <strong className="text-gray-900">
                    {selected.merchantName || selected.merchant?.merchantName || selected.createdByName || selected.userName || (selected.merchantId ? `Merchant ID: ${selected.merchantId}` : 'Merchant')}
                  </strong>
                </div>
                <div>
                  <span className="text-gray-500 text-xs block">Created Date & Time</span>
                  <strong className="text-gray-900">
                    {selected.createdAt ? `${formatDateTimeDDMMYYYY(selected.createdAt)} ${new Date(selected.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}` : 'N/A'}
                  </strong>
                </div>
              </div>
            )}

            <div className="flex gap-2 items-center">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="OPEN">Open</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="RESOLVED">Resolved</SelectItem>
                  <SelectItem value="CLOSED">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={handleStatusUpdate}>Update Status</Button>
            </div>
            <div className="p-3 bg-gray-50 rounded">
              <p className="text-xs font-semibold text-gray-500 mb-1">Description</p>
              <p className="text-sm text-gray-800">{selected?.description}</p>
            </div>
            <div className="space-y-2">
              {selected?.replies?.map((r) => (
                <div key={r.id} className={`p-3 rounded ${r.repliedByType === 'ADMIN' ? 'bg-blue-50 ml-8' : 'bg-gray-50 mr-8'}`}>
                  <p className="text-sm">{r.message}</p>
                  <p className="text-xs text-gray-500 mt-1">{r.repliedByName} • {formatDateTimeDDMMYYYY(r.createdAt)}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Textarea placeholder="Type reply..." value={reply} onChange={(e) => setReply(e.target.value)} />
              <Button onClick={handleReply}>Send</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTickets;
